import React, { Fragment, useState } from 'react';
import AdminDesignersCalendars from './AdminDesignersCalendars';
import { dayOfWeek2Text, dayOfWeekColor, isToday } from './Utils';
import cn from 'classnames';
import DesignerLine from './DesignerLine';
import MonthSelect from './MonthSelect';
import { getAdminCalendar } from './api/AdminCalendarApi';
import Calendar from './components/Calendar';
import { getStartOfWeek } from './components/Utils';
import { updateDesignerPriority } from './api/AdminDesignersApi';
import AddDesignerPopup from './components/AddDesignerPopup';

export default function AdminCalendar({ calendar, designers: designersInitial, days: daysInitial }) {
  const [designers, setDesigners] = useState(designersInitial);
  const [days, setDays] = useState(daysInitial);
  const [date, setDate] = useState(getStartOfWeek(new Date));
  const [designerCalendar, setDesignerCalendar] = useState(null);
  const [showAddDesignerPopup, setShowAddDesignerPopup] = useState(false);
  const [showDeactivated, setShowDeactivated] = useState(false);
  // designers whose priority was just changed here — keep them listed so setting someone
  // to D doesn't make the row disappear before it can be undone
  const [pinnedIds, setPinnedIds] = useState([]);

  // deactivated == disabled on this calendar (priority D/5) or a deactivated user account
  const isDeactivated = designer => designer.active === false || (designer.priority || 5) >= 5;
  // bookings only ever cover the requested period, so this means "has calls booked in the
  // shown weeks" — those count towards the day totals, so the row has to stay listed
  const hasBookings = designer => Object.keys(designer.bookings || {}).length > 0;
  const alwaysListed = designer => hasBookings(designer) || pinnedIds.includes(designer.id);
  // everyone still active is listed, availability or not; deactivated rows hide behind the toggle
  const hiddenDesigners = designers.filter(
    designer => isDeactivated(designer) && !alwaysListed(designer),
  );
  const visibleDesigners = showDeactivated
    ? designers
    : designers.filter(designer => !isDeactivated(designer) || alwaysListed(designer));

  const handleDateChange = newDate => {
    setDate(newDate);
    getAdminCalendar(
      calendar,
      newDate,
      data => {
        setDesigners(data.designers);
        setDays(data.days);
      },
      error => console.log(error),
    );
  };

  const closeAddDesignerPopup = () => {
    setShowAddDesignerPopup(false);
    // reload calendar and designers
    handleDateChange(date);
  };

  const handleDesignerClick = designer => {
    setDesignerCalendar(designer);
  };

  const toggleDeactivated = () => {
    const newValue = !showDeactivated;
    setShowDeactivated(newValue);
    // don't keep a hidden designer's calendar open
    if (
      !newValue &&
      designerCalendar &&
      isDeactivated(designerCalendar) &&
      !alwaysListed(designerCalendar)
    ) {
      setDesignerCalendar(null);
    }
  };

  const onUpdateDesignerPriority = designer => {
    return priority => {
      updateDesignerPriority(calendar, designer, priority, () => {}, () => {});
      if (!pinnedIds.includes(designer.id)) setPinnedIds([...pinnedIds, designer.id]);
      const newDesigners = [...designers];
      const designerToUpdate = newDesigners.find(dsg => dsg.id === designer.id);
      designerToUpdate.priority = priority;
      setDesigners(newDesigners);
    };
  };

  return (
    <Fragment>
      <div className="calendar-header">
        <h2 className='title'>Designers Availability</h2>
        <MonthSelect handleChange={handleDateChange} />
      </div>
      <div className="designer-calendars_wrapper">
        <div className="caledars-panel_left">
          <div key={calendar.id} className="calendar">
            <div className="calendar-days">
              <div className="calendar-add-designer">
                <button type='button' className='btn -empty -y2023' onClick={() => setShowAddDesignerPopup(true)}>Manage</button>
                {hiddenDesigners.length > 0 && (
                  <label className="show-deactivated">
                    <input type="checkbox" checked={showDeactivated} onChange={toggleDeactivated} />
                    {` Show deactivated (${hiddenDesigners.length})`}
                  </label>
                )}
              </div>
            </div>
            <div className="calendar-designers">
              {visibleDesigners.map(designer => (
                <DesignerLine
                  key={`designer-${designer.id}`}
                  designer={designer}
                  updateDesignerPriority={onUpdateDesignerPriority(designer)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="calendar-panel_right">
          <div key={calendar.id} className="calendar">
            <div className="calendar-days">
              {days.map(day => (
                <div key={`${day.day}${day.dom}${day.year}`} className={cn('calendar-day', '-header', dayOfWeekColor(day.dow), {'-today': isToday(day)})} >
                  <div className="total">
                    <span className={cn({'highlight': day.total.slots})}>{day.total.booked}</span>
                    <span>{` / ${day.total.slots}`}</span>
                  </div>
                  <div className="dow">{dayOfWeek2Text(day.dow)}</div>
                  <div className="day">{day.dom}</div>
                  {/*<div className="month">{month2Text(day.month)}</div>*/}
                </div>
              ))}
            </div>
            <div className="calendar-designers">
              {visibleDesigners.map(designer => (
                <AdminDesignersCalendars key={designer.id} designer={designer} handleDesignerClick={handleDesignerClick} days={days} selected={designerCalendar && designer && designer.id == designerCalendar.id} />
              ))}
            </div>
          </div>
        </div>
      </div>
      {designerCalendar && (
        <Fragment>
          <Calendar admin={true} calendar={calendar} designer={designerCalendar} date={date} name={designerCalendar.full_name} availabilityUrl={designerCalendar.availability_url} />
        </Fragment>
      )}
      {showAddDesignerPopup && (
        <AddDesignerPopup designers={designers} calendar={calendar} onCloseModal={closeAddDesignerPopup} />
      )}
    </Fragment>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/designer_calendars/index.js